/**
 * UserFeedback Component
 * Displays user-friendly messages for cart operations, especially inventory validation errors
 */

import React, { useEffect, useState } from 'react';

const UserFeedback = ({ 
  feedback, 
  onClear, 
  autoHideDelay = 5000,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (feedback) {
      setIsVisible(true);
      setIsClosing(false);

      // Auto-hide for success and info messages
      if (feedback.type === 'success' || feedback.type === 'info') {
        const timer = setTimeout(() => {
          handleClose();
        }, autoHideDelay);

        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
      setIsClosing(false);
    }
  }, [feedback, autoHideDelay]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onClear?.();
    }, 300); // Animation duration
  };

  if (!feedback || !isVisible) {
    return null;
  }

  const getAlertClass = () => {
    const baseClass = 'alert';
    const typeClass = {
      success: 'alert-success',
      error: 'alert-danger',
      warning: 'alert-warning',
      info: 'alert-info'
    }[feedback.type] || 'alert-secondary';

    return `${baseClass} ${typeClass}`;
  };

  const getIcon = () => {
    const iconMap = {
      success: 'bi-check-circle',
      error: 'bi-exclamation-triangle',
      warning: 'bi-exclamation-triangle',
      info: 'bi-info-circle'
    };

    return iconMap[feedback.type] || 'bi-info-circle';
  };

  const renderActionButtons = () => {
    if (!feedback.action) return null;

    switch (feedback.action) {
      case 'inventory_adjusted':
        return (
          <div className="mt-2">
            <small className="text-muted d-block">
              Your cart has been automatically adjusted to the maximum available quantity.
            </small>
            {feedback.availableQuantity && (
              <small className="text-muted d-block">
                Available: {feedback.availableQuantity} units
                {feedback.requestedQuantity && ` (you requested ${feedback.requestedQuantity})`}
              </small>
            )}
          </div>
        );

      case 'inventory_limit':
        return (
          <div className="mt-2">
            <small className="text-muted d-block">
              The maximum available quantity has been added to your cart.
            </small>
            {feedback.availableQuantity && (
              <small className="text-muted d-block">
                Available: {feedback.availableQuantity} units
              </small>
            )}
          </div>
        );

      case 'retry':
        return (
          <div className="mt-2">
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={handleClose}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Try Again
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className={`user-feedback ${className} ${isClosing ? 'user-feedback--closing' : ''}`}
      role="alert"
      aria-live="polite"
    >
      <div className={getAlertClass()}>
        <div className="d-flex align-items-start">
          <div className="flex-shrink-0 me-2">
            <i className={`bi ${getIcon()}`}></i>
          </div>
          
          <div className="flex-grow-1">
            <div className="user-feedback__message">
              {feedback.message}
            </div>
            
            {renderActionButtons()}
          </div>

          {/* Manual close button for warnings and errors */}
          {(feedback.type === 'warning' || feedback.type === 'error') && (
            <button 
              type="button" 
              className="btn-close" 
              aria-label="Close"
              onClick={handleClose}
            ></button>
          )}
        </div>
      </div>

      <style>{`
        .user-feedback {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          min-width: 300px;
          animation: slideIn 0.3s ease-out;
        }

        .user-feedback--closing {
          animation: slideOut 0.3s ease-in;
        }

        .user-feedback__message {
          font-weight: 500;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        /* Mobile responsiveness */
        @media (max-width: 576px) {
          .user-feedback {
            top: 10px;
            right: 10px;
            left: 10px;
            max-width: none;
            min-width: auto;
          }

          @keyframes slideIn {
            from {
              transform: translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          @keyframes slideOut {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(-100%);
              opacity: 0;
            }
          }
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .alert {
            border-width: 2px;
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .user-feedback,
          .user-feedback--closing {
            animation: none;
          }

          .user-feedback {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default UserFeedback;